import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTokens } from '../../shared/theme';
import { verifyMagicLink, resolvePortalHome } from '../../shared/services/auth';
import Spinner from '../../shared/Spinner.jsx';
import AuthScreen from '../AuthScreen.jsx';
import { schoolPortalUrl } from '../portalUrl.js';

const replaceLocation = (url) => { window.location.replace(url); };

/**
 * Magic-link landing for the portal (2026-07-21): the emailed link points at
 * `<school>.coffeechats.me/auth/verify?token=...` (returnOrigin passed at
 * request time; open-redirect-guarded server-side). Exchange the token for a
 * cookie session, resolve which school this account administers, and hop
 * there — mirroring AuthHop's Google return leg. Errors land on a quiet
 * card with a path back to sign-in.
 *
 * The token is redeemed only on a press, never on load: inbox link scanners
 * open emailed links in a sandbox browser that runs this page's JS, and a
 * redeem-on-load let them spend the single-use token before the admin did.
 */
export default function MagicLinkVerify({ onRedirect = replaceLocation }) {
  const t = useTokens();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [error, setError] = useState(
    token ? null : 'This sign-in link is incomplete. Request a new one from the sign-in page.',
  );
  const [verifying, setVerifying] = useState(false);
  const started = useRef(false);

  const handleContinue = () => {
    if (started.current) { return; }
    started.current = true;
    setVerifying(true);
    (async () => {
      try {
        await verifyMagicLink(token);
        const { slug } = await resolvePortalHome();
        if (!slug) {
          setError('We couldn’t match your account to a Coffee Chats school. Ask your school’s administrator for an invite.');
          return;
        }
        onRedirect(schoolPortalUrl(slug, window.location, '/overview'));
      } catch (err) {
        const status = err?.response?.status;
        setError(
          status === 400 || status === 404
            ? 'This sign-in link is invalid or has expired. Request a new one from the sign-in page.'
            : 'Sign-in failed. Please try again from the sign-in page.',
        );
      }
    })();
  };

  if (error) {
    return (
      <AuthScreen>
        <h1
          className="text-center text-[34px] leading-tight"
          style={{ fontFamily: t.fonts.display, fontWeight: 500, color: t.heading }}
        >
          Link expired
        </h1>
        <div
          className="mt-8 rounded-2xl border p-6 shadow-[0_10px_34px_rgba(42,23,16,0.06)]"
          style={{ backgroundColor: t.surface, borderColor: t.border }}
        >
          <p className="text-base leading-relaxed" style={{ color: t.muted, fontFamily: t.fonts.body }}>
            {error}
          </p>
          <a
            href="/auth"
            className={`mt-5 flex min-h-[48px] w-full items-center justify-center rounded-[10px] text-base font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
            style={{ backgroundColor: '#111111', color: '#FFFBF6', fontFamily: t.fonts.body }}
          >
            Back to sign in
          </a>
        </div>
      </AuthScreen>
    );
  }

  if (!verifying) {
    return (
      <AuthScreen>
        <h1
          className="text-center text-[34px] leading-tight"
          style={{ fontFamily: t.fonts.display, fontWeight: 500, color: t.heading }}
        >
          Sign in to Coffee Chats
        </h1>
        <div
          className="mt-8 rounded-2xl border p-6 shadow-[0_10px_34px_rgba(42,23,16,0.06)]"
          style={{ backgroundColor: t.surface, borderColor: t.border }}
        >
          <p className="text-base leading-relaxed" style={{ color: t.muted, fontFamily: t.fonts.body }}>
            Your sign-in link is ready.
          </p>
          <button
            type="button"
            onClick={handleContinue}
            className={`mt-5 flex min-h-[48px] w-full items-center justify-center rounded-[10px] text-base font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
            style={{ backgroundColor: '#111111', color: '#FFFBF6', fontFamily: t.fonts.body }}
          >
            Continue to Coffee Chats
          </button>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <div className="flex items-center justify-center gap-4" role="status" aria-live="polite">
        <Spinner size={22} />
        <p className="text-[19px]" style={{ color: t.body, fontFamily: t.fonts.display }}>
          Signing you in...
        </p>
      </div>
    </AuthScreen>
  );
}
